import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert,
  Pagination, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Card, CardContent, IconButton, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import {
  ArrowBack, Visibility, History, Image as ImageIcon,
  CheckCircle, Cancel, AccessTime, Block
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getMyTopupRequests, cancelTopupRequest, getEvidencePhotoUrl } from 'api/wallet';
import AuthenticatedImage from 'components/AuthenticatedImage';

const TopupRequests = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [imageDialog, setImageDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    loadTopupRequests(currentPage);
  }, [currentPage]);

  const loadTopupRequests = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const result = await getMyTopupRequests(page, 10);
      setRequests(result.data.requests);
      setPagination(result.data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load top-up requests');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!selectedRequest) return;
    try {
      setCancelLoading(true);
      await cancelTopupRequest(selectedRequest._id);
      setSuccess('Top-up request cancelled successfully');
      setCancelDialog(false);
      loadTopupRequests(currentPage);
    } catch (err) {
      setError(err.message || 'Failed to cancel request');
    } finally {
      setCancelLoading(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbols = { USD: '$', THB: '฿', LAK: '₭' };
    return `${symbols[currency] || ''}${amount.toLocaleString()}`;
  };

  const getCurrencyFlag = (currency) => {
    const map = { USD: 'us', THB: 'th', LAK: 'la' };
    return (
      <img
        src={`https://flagcdn.com/16x12/${map[currency] || 'un'}.png`}
        alt={currency}
        style={{ width: 16, height: 12, marginLeft: 4, verticalAlign: 'middle', borderRadius: 1 }}
      />
    );
  };

  const getStatusColor = (status) => {
    const map = { approved: 'success', rejected: 'error', pending: 'warning', cancelled: 'default' };
    return map[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const map = { approved: <CheckCircle />, rejected: <Cancel />, pending: <AccessTime />, cancelled: <Block /> };
    return map[status] || null;
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (loading && currentPage === 1) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading requests...</Typography>
      </Box>
    );
  }

  return (
    <Box m={2}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button color="secondary" startIcon={<ArrowBack />} onClick={() => navigate('/customer-portal/wallet')} sx={{ mr: 2 }}>
          Back to Wallet
        </Button>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <History sx={{ mr: 1 }} />
          My Top-up Requests
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper>
        {requests.length === 0 ? (
          <Box textAlign="center" py={6}>
            <History sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>No top-up requests found</Typography>
            <Button variant="contained" color="secondary" onClick={() => navigate('/customer-portal/wallet/topup')}>
              Create First Request
            </Button>
          </Box>
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {requests.map((request) => (
                    <Grid item xs={12} key={request._id}>
                      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box>
                              <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                                <Typography variant="h6" fontWeight="bold">
                                  {formatCurrency(request.amount, request.currency)}
                                </Typography>
                                {getCurrencyFlag(request.currency)}
                              </Box>
                              <Chip
                                icon={getStatusIcon(request.status)}
                                label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                color={getStatusColor(request.status)}
                                size="small"
                              />
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              {request.evidencePhoto && (
                                <Tooltip title="View Evidence Photo">
                                  <IconButton size="small" onClick={() => { setSelectedRequest(request); setImageDialog(true); }}>
                                    <ImageIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Button
                                color="secondary" size="small" variant="outlined"
                                startIcon={<Visibility />}
                                onClick={() => { setSelectedRequest(request); setDetailsDialog(true); }}
                              >
                                Details
                              </Button>
                              {request.status === 'pending' && (
                                <Button
                                  color="error" size="small" variant="outlined"
                                  startIcon={<Cancel />}
                                  onClick={() => { setSelectedRequest(request); setCancelDialog(true); }}
                                >
                                  Cancel
                                </Button>
                              )}
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Submitted: {formatDate(request.createdAt)}
                          </Typography>
                          {request.description && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {request.description}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Request ID</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Evidence</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request._id} hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={request._id}
                          >
                            {request._id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography fontWeight="bold">{formatCurrency(request.amount, request.currency)}</Typography>
                            {getCurrencyFlag(request.currency)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(request.status)}
                            label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                            color={getStatusColor(request.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(request.createdAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          {request.evidencePhoto && (
                            <Tooltip title="View Evidence Photo">
                              <IconButton size="small" onClick={() => { setSelectedRequest(request); setImageDialog(true); }}>
                                <ImageIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Button
                              color="secondary" size="small" startIcon={<Visibility />}
                              onClick={() => { setSelectedRequest(request); setDetailsDialog(true); }}
                            >
                              Details
                            </Button>
                            {request.status === 'pending' && (
                              <Button
                                color="error" size="small" variant="outlined" startIcon={<Cancel />}
                                onClick={() => { setSelectedRequest(request); setCancelDialog(true); }}
                              >
                                Cancel
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {pagination.pages > 1 && (
              <Box display="flex" justifyContent="center" p={3}>
                <Pagination count={pagination.pages} page={currentPage} onChange={(_, p) => setCurrentPage(p)} color="secondary" />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Request Details Dialog */}
      <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Request Information</Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        Amount: {formatCurrency(selectedRequest.amount, selectedRequest.currency)}
                      </Typography>
                      {getCurrencyFlag(selectedRequest.currency)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Status: <Chip icon={getStatusIcon(selectedRequest.status)} label={selectedRequest.status} color={getStatusColor(selectedRequest.status)} size="small" />
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Submitted: {formatDate(selectedRequest.createdAt)}
                    </Typography>
                    {selectedRequest.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Note: {selectedRequest.description}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                {selectedRequest.status === 'approved' && selectedRequest.reviewedBy && (
                  <Card variant="outlined" sx={{ borderColor: 'success.main', bgcolor: 'success.50' }}>
                    <CardContent>
                      <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>Approved</Typography>
                      <Typography variant="body2">By: {selectedRequest.reviewedBy.name}</Typography>
                      <Typography variant="body2">On: {formatDate(selectedRequest.reviewedBy.reviewedAt)}</Typography>
                      {selectedRequest.approvalNotes && (
                        <Typography variant="body2">Notes: {selectedRequest.approvalNotes}</Typography>
                      )}
                    </CardContent>
                  </Card>
                )}
                {selectedRequest.status === 'rejected' && (
                  <Card variant="outlined" sx={{ borderColor: 'error.main', bgcolor: 'error.50' }}>
                    <CardContent>
                      <Typography variant="h6" color="error.main" sx={{ mb: 1 }}>Rejected</Typography>
                      {selectedRequest.reviewedBy && (
                        <>
                          <Typography variant="body2">By: {selectedRequest.reviewedBy.name}</Typography>
                          <Typography variant="body2">On: {formatDate(selectedRequest.reviewedBy.reviewedAt)}</Typography>
                        </>
                      )}
                      {selectedRequest.rejectionReason && (
                        <Typography variant="body2">Reason: {selectedRequest.rejectionReason}</Typography>
                      )}
                    </CardContent>
                  </Card>
                )}
                {selectedRequest.status === 'cancelled' && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Cancelled</Typography>
                      {selectedRequest.cancelledAt && (
                        <Typography variant="body2">On: {formatDate(selectedRequest.cancelledAt)}</Typography>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Grid>

              {selectedRequest.evidencePhoto && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>Evidence Photo</Typography>
                      <AuthenticatedImage
                        src={getEvidencePhotoUrl(selectedRequest.evidencePhoto.filename)}
                        alt="Evidence"
                        style={{ width: '100%', height: 'auto', maxHeight: 300, objectFit: 'contain', borderRadius: 4 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRequest?.status === 'pending' && (
            <Button
              color="error" variant="outlined" startIcon={<Cancel />}
              onClick={() => { setDetailsDialog(false); setCancelDialog(true); }}
            >
              Cancel Request
            </Button>
          )}
          <Button color="secondary" onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Evidence Image Dialog */}
      <Dialog open={imageDialog} onClose={() => setImageDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Evidence Photo</DialogTitle>
        <DialogContent>
          {selectedRequest?.evidencePhoto && (
            <AuthenticatedImage
              src={getEvidencePhotoUrl(selectedRequest.evidencePhoto.filename)}
              alt="Evidence"
              style={{ width: '100%', height: 'auto', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialog(false)} color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel color="warning" />
          Cancel Top-up Request
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to cancel this top-up request?</Typography>
          {selectedRequest && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Amount: {formatCurrency(selectedRequest.amount, selectedRequest.currency)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Submitted: {formatDate(selectedRequest.createdAt)}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" variant="outlined" onClick={() => setCancelDialog(false)} disabled={cancelLoading}>
            Keep Request
          </Button>
          <Button
            color="error" variant="contained" onClick={handleCancelConfirm}
            disabled={cancelLoading}
            startIcon={cancelLoading ? <CircularProgress size={18} /> : <Cancel />}
          >
            {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TopupRequests;
